import { Fragment, useMemo, useState } from "react";
import { PERIOD_OPTIONS } from "../utils/analytics.js";
import { formatDateRange } from "../utils/format.js";
import { parseUsername } from "../utils/api.js";
import { accentAt, accentVars } from "../utils/palette.js";
import { MAX_COMPARE_PROFILES } from "../utils/url.js";
import ComparisonKPIGrid from "./ComparisonKPIGrid.jsx";
import ComparisonMuscles from "./ComparisonMuscles.jsx";
import ComparisonEquipment from "./ComparisonEquipment.jsx";
import ComparisonExercises from "./ComparisonExercises.jsx";
import ComparisonTrend from "./ComparisonTrend.jsx";
import Avatar from "./Avatar.jsx";
import styles from "./ComparisonScreen.module.css";

function ProfileChip({ profile, accent, onRemove }) {
  const p = profile?.preview?.normalized?.profile || {};
  const name = p.fullName || profile.username;
  return (
    <div className={styles.chip} style={accentVars(accent)}>
      <Avatar imageUrl={p.imageUrl} name={name} seed={profile.username} size={32} accent={accent.color} />
      <div className={styles.chipText}>
        <span className={styles.chipName}>{name}</span>
        {profile.status === "loading" && <span className={styles.chipState}>importing…</span>}
        {profile.status === "error" && <span className={styles.chipError} title={profile.error}>could not load</span>}
        {profile.status === "ready" && <span className={styles.chipHandle}>@{profile.username}</span>}
      </div>
      {onRemove && (
        <button
          className={styles.chipRemove}
          type="button"
          onClick={onRemove}
          aria-label={`Remove @${profile.username} from the comparison`}
        >
          ×
        </button>
      )}
    </div>
  );
}

export default function ComparisonScreen({
  profiles,
  periodDays,
  onPeriodChange,
  onAddProfile,
  onRemoveProfile,
  onExit,
  onNewSearch
}) {
  const [showAdd, setShowAdd] = useState(false);
  const [addInput, setAddInput] = useState("");
  const [addError, setAddError] = useState("");

  // Memoised so typing in the add field does not hand the charts a new set of
  // profile objects and make them redraw on every keystroke.
  const withAccent = useMemo(() => profiles.map((profile, i) => ({ ...profile, accent: accentAt(i) })), [profiles]);
  const ready = useMemo(() => withAccent.filter((profile) => profile.status === "ready" && profile.analytics), [withAccent]);
  const weightUnit = profiles[0].weightUnit;
  const dateRange = formatDateRange(profiles[0].analytics?.periods?.current);
  const canAdd = profiles.length <= MAX_COMPARE_PROFILES;
  const adding = profiles.some((profile) => profile.status === "loading");

  function handleAdd(e) {
    e.preventDefault();
    const parsed = parseUsername(addInput);
    if (!parsed || parsed.length < 2) {
      setAddError("Enter a valid Hevy username.");
      return;
    }
    if (profiles.some((profile) => profile.username.toLowerCase() === parsed.toLowerCase())) {
      setAddError("Already in this comparison.");
      return;
    }
    setAddError("");
    setAddInput("");
    setShowAdd(false);
    onAddProfile(parsed, { periodDays });
  }

  return (
    <div id="screen-comparison" className={styles.root}>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <div className={`${styles.profiles} ${profiles.length > 2 ? styles.profilesMany : ""}`}>
            {withAccent.map((profile, i) => (
              <Fragment key={profile.username}>
                {i > 0 && <span className={styles.vs}>vs</span>}
                <ProfileChip
                  profile={profile}
                  accent={profile.accent}
                  onRemove={i > 0 ? () => onRemoveProfile(profile.username) : undefined}
                />
              </Fragment>
            ))}

            {canAdd && (showAdd ? (
              <form className={styles.addForm} onSubmit={handleAdd} noValidate>
                <input
                  className={styles.addInput}
                  type="text"
                  value={addInput}
                  onChange={(e) => { setAddInput(e.target.value); setAddError(""); }}
                  placeholder="@username"
                  autoComplete="off"
                  spellCheck={false}
                  disabled={adding}
                  autoFocus
                />
                <button className={styles.addSubmit} type="submit" disabled={adding || !addInput.trim()}>Add</button>
                <button
                  className={styles.addCancel}
                  type="button"
                  onClick={() => { setShowAdd(false); setAddError(""); }}
                  aria-label="Cancel"
                >
                  ×
                </button>
                {addError && <span className={styles.addError} role="alert">{addError}</span>}
              </form>
            ) : (
              <button className={styles.addOpen} type="button" onClick={() => setShowAdd(true)}>+ Add</button>
            ))}

            {dateRange && <span className={styles.dateRange}>{dateRange}</span>}
          </div>

          <div className={styles.controls}>
            <div className={styles.periodGroup} role="group" aria-label="Time period">
              {PERIOD_OPTIONS.map((days) => (
                <button
                  key={days}
                  className={`${styles.periodBtn} ${periodDays === days ? styles.active : ""}`}
                  onClick={() => onPeriodChange(days)}
                  aria-pressed={periodDays === days}
                >
                  {days === 365 ? "1y" : `${days}d`}
                </button>
              ))}
            </div>
            <button className={styles.exitBtn} onClick={onExit}>
              ← Back
            </button>
            <button className={styles.newSearch} onClick={onNewSearch}>
              <span className={styles.newSearchCompact}>Search</span>
              <span className={styles.newSearchFull}>New Search</span>
            </button>
          </div>
        </div>
      </header>

      <main className={styles.main}>
        {ready.length >= 2 && (
          <>
            <ComparisonKPIGrid profiles={ready} weightUnit={weightUnit} periodDays={periodDays} />

            <ComparisonTrend profiles={ready} periodDays={periodDays} />

            <div className={`${styles.splitRow} ${ready.length > 2 ? styles.splitStacked : ""}`}>
              <ComparisonMuscles profiles={ready} periodDays={periodDays} />
              <ComparisonEquipment profiles={ready} />
            </div>

            <ComparisonExercises profiles={ready} weightUnit={weightUnit} periodDays={periodDays} />
          </>
        )}
      </main>
    </div>
  );
}
